import { apiClient } from './client';
import type { PaginatedResponse } from './types';

export interface DiseaseTreatment {
  generic: string;
  pcpb: string | null;
  application_notes?: string;
}

/**
 * Bilingual disease card snapshot matched by the API at scan time
 * (DiseaseCardResolver). Shape mirrors resources/content/crops/*.json
 * disease entries — keep unknown keys tolerant so content evolution
 * doesn't break the PWA.
 */
export interface DiseaseCard {
  key?: string;
  name_en?: string;
  name_sw?: string;
  type?: string;
  severity_classes?: string;
  symptoms_en?: string;
  symptoms_sw?: string;
  treatment_en?: string;
  treatment_sw?: string;
  prevention_en?: string;
  prevention_sw?: string;
  scanner_notes?: string;
  [k: string]: unknown;
}

export interface DiseaseSuggestion {
  name: string;
  probability: number;
}

export interface DiseaseDetection {
  id: string;
  season_id: string | null;
  crop_id: string | null;
  image_url: string | null;
  image_urls?: string[] | null;
  provider: 'mock' | 'crop_health' | 'offline_decision_tree';
  top_diagnosis: string | null;
  confidence: number | null;
  treatments: DiseaseTreatment[] | null;
  disease_card: DiseaseCard | null;
  suggestions: DiseaseSuggestion[] | null;
  opt_in_for_training: boolean;
  captured_at: string | null;
}

export interface DetectPayload {
  image?: File;
  images?: File[];
  crop_id?: string;
  season_id?: string;
  opt_in_for_training?: boolean;
}

export interface PlanTreatmentPayload {
  season_id: string;
  treatment_index: number;
  amount_kes: number;
  supplier_name?: string;
  incurred_at?: string;
}

export interface DiseaseFeedbackPayload {
  verdict: 'correct' | 'incorrect';
  correction_key?: string;
  correction_text?: string;
}

export const diseaseApi = {
  async detect(payload: DetectPayload): Promise<{ data: DiseaseDetection }> {
    const fd = new FormData();
    // Legacy single-photo field for older builds; `images[]` is the
    // Phase B multi-photo field (API accepts either).
    if (payload.image) fd.append('image', payload.image);
    if (payload.images) {
      for (const img of payload.images) fd.append('images[]', img);
    }
    if (payload.crop_id) fd.append('crop_id', payload.crop_id);
    if (payload.season_id) fd.append('season_id', payload.season_id);
    if (payload.opt_in_for_training) fd.append('opt_in_for_training', '1');

    const { data } = await apiClient.post<{ data: DiseaseDetection }>(
      '/disease/detect',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async history(): Promise<PaginatedResponse<DiseaseDetection>> {
    const { data } = await apiClient.get<PaginatedResponse<DiseaseDetection>>(
      '/disease/history',
    );
    return data;
  },

  async report(id: string): Promise<Blob> {
    const res = await apiClient.get(`/disease/${id}/report`, { responseType: 'blob' });
    return res.data as Blob;
  },

  async planTreatment(id: string, payload: PlanTreatmentPayload): Promise<{ data: unknown }> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/disease/${id}/plan-treatment`,
      payload,
    );
    return data;
  },

  async feedback(id: string, payload: DiseaseFeedbackPayload): Promise<{ data: unknown }> {
    const { data } = await apiClient.post<{ data: unknown }>(`/disease/${id}/feedback`, payload);
    return data;
  },
};
